<template>
  <div
      v-if="isLoading"
      id="wrapper"
      :style="wrapStyle"
      @mousemove="interactiveMethods.onMousemove"
      @mousedown="interactiveMethods.onMousemove"
      @mouseup="interactiveMethods.onMouseup"
      @touchstart="interactiveMethods.onTouchMove"
      @touchmove="interactiveMethods.onTouchMove"
      @touchend="interactiveMethods.onTouchEnd"
  >
    <div class="lectureWrap" :class="{on : learningStore.isHideBarsToggle}">
      <section class="assessmentHeader">
        <LazyLearningLevelAssessmentHeaderTop
            :track-title="assessmentStore.trackInfo.title"
            :track-level-title="assessmentStore.trackInfo.trackLevelTitle"
            @on-click-quit="methods.onClickQuit"
        />
        <LazyLearningLevelAssessmentHeaderBottom
            :level-assessment="levelAssessment"
            :track-learning-time="learningStore.task.learningTime"
            :is-in-progress="true"
        />
      </section>
      <LazyLearningMiddle ref="middle" class="assessmentMiddle"/>
      <LazyLearningLevelAssessmentFooter :is-in-progress="true" />
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  LazyLearningLevelAssessmentFooter,
  LazyLearningLevelAssessmentHeaderBottom,
  LazyLearningLevelAssessmentHeaderTop,
  LazyLearningMiddle
} from "#components";
import {computed, onBeforeMount, onBeforeUnmount, onMounted, ref, useI18n, useRoute, useRouter} from "#imports";
import {useUserStore} from "~/store/user/userStore";
import {useLearningStore} from "~/store/learning/learningStore";
import {useLearningAssessmentStore} from "~/store/learning/assessment/learningAssessmentStore";
import {onBeforeRouteLeave} from "vue-router";
import {TaskModel} from "~~/package/mapper/learning/model/TaskModel";
import {useLevelAssessmentService} from "~/composables/service/useLevelAssessmentService";
import {TrackLearningType} from "~~/package/mapper/track/model/TrackLearningType";
import {useLearningWindowEventService} from "~/composables/service/useLearningWindowEventService";
import {useGenericDialog} from "~/composables/useGenericDialog";

// TSK-11215: no sessionOwner meta here on purpose. This is not a SkillCertify assessment — the
// termination popup's copy and i18n keys (SkillCertify-duplicate-session-warning-*) are
// SkillCertify-specific, and this route has no assessmentId param, so the popup's 확인 button
// would send the user to "/" instead of back to what they were doing.

const { t } = useI18n();

const route = useRoute();
const router = useRouter();
const userStore = useUserStore();
const learningStore = useLearningStore();
    const assessmentStore = useLearningAssessmentStore();
const learningWindowEventService = useLearningWindowEventService();
const { openWrongApproachAlert, openGenericConfirm } = useGenericDialog();

const middle = ref();
const isLoading = ref(false);
const paramTaskTemplateId = parseInt(route.params.taskTemplateId as string);
const levelId = parseInt(route.params.levelId as string);

const levelAssessment = computed(() =>
    `${assessmentStore.trackInfo.trackLevelTitle} ${t('skillCertify-assessment', 1)}`
);

const finishPath = `/learning/levelAssessment/result/${(route.params.trackId as string)}/${(route.params.levelId as string)}`;

onMounted(() => {
  learningStore.isNeverRun = true;
  learningStore.isChangedButNotSubmit = false;
  if (methods.checkValidApproach()) methods.getTrackData();
  else {
    goToStartPage();
    methods.openFailPopup();
  }
});

onBeforeMount(() => {
  learningStore.resetCodeData();
  learningStore.resetContestData();
  learningStore.resetAssessment();
  learningStore.resetProSkillAssure();
  learningWindowEventService.addEventListener();
  window.addEventListener("unload", methods.onLoadHandler);
});

onBeforeUnmount(() => {
  learningWindowEventService.removeEventListener();
  window.removeEventListener("unload", methods.onLoadHandler);
});

onBeforeRouteLeave((to, from) => {
  let isPageMove = false;

  if (to.fullPath === finishPath || to.fullPath === "/dashboard") isPageMove = true;
  if (to.params.taskId && methods.isNext(parseInt(to.params.taskId as string))) isPageMove = true;
  else if (assessmentStore.levelAssessmentIdList.length === 0) isPageMove = true;

  if(isPageMove) {
    clearInterval(assessmentStore.levelAssessment.timerIntervalId);
    learningStore.resetAll();
  }
  else methods.onClickQuit();

  return isPageMove;
});

const methods = {
  onLoadHandler() {
    methods.submitSolvedTime(false, false);
  },
  checkValidApproach() {
    if (!userStore.isLogin()) methods.goToHomePage();
    else if (assessmentStore.levelAssessmentIdList.length < 1) return false;
    else return assessmentStore.levelAssessmentIdList.includes(paramTaskTemplateId);
  },
  goToHomePage() {
    router.replace({path: "/"});
  },
  openFailPopup() {
    openWrongApproachAlert();
  },
  getTrackData() {
    if (learningStore.trackLevel) methods.getTaskData(paramTaskTemplateId);
    else useLevelAssessmentService().getLevelAssessmentInfo(levelId, false)
        .then((levelAssessment) => {
          learningStore.updateTrackLevelInfo(levelAssessment, levelId.toString())
          methods.getTaskData(paramTaskTemplateId);
        }).catch(() => methods.openFailPopup());
  },
  getTaskData(taskTemplateId: number) {
    useLevelAssessmentService().getLevelAssessmentTasksWithLatestCodes(taskTemplateId, levelId)
        .then((levelAssessmentTask: TaskModel) => {
          learningStore.updateLearningData(levelAssessmentTask);
          isLoading.value = true;
        }).catch(() => methods.openFailPopup());
  },
  onClickQuit(goToStartPage = false) {
    openGenericConfirm({
      title: t("learning-leave-warning-title"),
      contents: t("learning-leave-warning-description"),
    }, {
      confirm: {
        title: t("learning-leave"),
        event: () => {
          methods.submitSolvedTime(true, goToStartPage);
        }
      },
      close: {
        title: t("common-cancel"),
        isWide: false,
        event: () => {}
      },
    });
  },
  isNext(to: number) {
    return to === learningStore.getNextLevelAssessmentId(paramTaskTemplateId);
  },
  submitSolvedTime(isPageRoute: boolean, isGoToStartPage: boolean) {
    useLevelAssessmentService().submitAnswer(
        parseInt(route.params.levelId as string),
        parseInt(route.params.taskTemplateId as string),
        {
          action: "exit",
          questionIdList: [parseInt(route.params.taskTemplateId as string)],
          submit: {
            taskSolveTime: assessmentStore.taskSolveTime,
            isLastQuestion: false,
            output: "",
            submissionAnswerList: [],
          }
        }
    ).finally(() => {
      learningStore.resetLevelAssessment();
      if (isPageRoute) {
        if(isGoToStartPage) {
          goToStartPage();
        } else {
          router.replace({path: `/dashboard/skillAdvance/practice/${route.params.trackId}`, query: {type: TrackLearningType.ASSESSMENT}});
        }
      }
    });
  }
}

const goToStartPage = () => {
  router.replace({
    name: "learning-levelAssessment-trackId-levelId",
    params: {
      trackId: route.params.trackId,
      levelId: route.params.levelId
    }
  })
}

const interactiveMethods = {
  onMousemove(event: MouseEvent) {
    if (learningStore.draggingActivityWidget.id !== -1 || learningStore.isChangeActivityMenuHeight) {
      learningStore.mousePosition = {x: event.clientX, y: event.clientY}
      interactiveMethods.setContentPosition();
    }
  },
  onMouseup(event: MouseEvent) {
    learningStore.isChangeActivityMenuHeight = false;
    if (learningStore.draggingActivityWidget.id !== -1) {
      const isInteractionWidget = (event.target as HTMLElement).getAttribute("is-interaction-widget")
      if (
          learningStore.draggingActivityWidget.isOrderWidget
          && learningStore.draggingActivityWidget.isSelectedCard
          && !isInteractionWidget
      ) {
        const index = learningStore.task.markOrderList.findIndex(order => order.id === learningStore.draggingActivityWidget.id)
        learningStore.task.markOrderList[index].answerIndex = -1

        learningStore.isOrderMenuOpen = true;
        learningStore.isSelectedSelectionId = -1;
      }

      learningStore.draggingActivityWidget = {
        isOrderWidget: false,
        isSelectedCard: false,
        id: -1,
      }
      learningStore.mousePosition = {x: -1, y: -1}
    }
  },
  onTouchMove(event: TouchEvent) {
    if (learningStore.isChangeActivityMenuHeight) {
      if (event.touches && event.touches.length > 0) {
        learningStore.mousePosition = {x: event.touches[0].clientX, y: event.touches[0].clientY}
      }
      interactiveMethods.setContentPosition();
    }
  },
  onTouchEnd() {
    learningStore.isChangeActivityMenuHeight = false;
  },
  setContentPosition() {
    if (learningStore.isChangeActivityMenuHeight) {
      learningStore.contentPosition = {
        height: middle.value.$el.clientHeight,
        top: middle.value.$el.getBoundingClientRect().top
      }
    }
  }
}

const wrapStyle = computed(() => {
  if (learningStore.draggingActivityWidget.id !== -1) {
    return {
      userSelect: 'none',
      cursor: 'grabbing'
    }
  }
  return {}
})
</script>
